import { Avatar as ShadcnAvatar, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import Image from "next/image"
import { EmptyAvatar } from "./Empty"
import { BaseUserPublic, User } from "@/types/users"

const ONE_PIXEL_SVG = `data:image/svg+xml,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1">
    <rect width="1" height="1" fill="#E5E7EB"/>
  </svg>
  `)}`

type Props = {
  /**
   * The src of the image to display
   */
  src?: string
  /**
   * The alt text of the image to display.
   */
  alt?: string
  /**
   * Size of the avatar in pixels
   */
  size: number

  /**
   * Any additional classes to apply to the Avatar
   */
  className?: string
  /**
   * Any additional classes to apply tot he fallback.
   */
  fallbackClassName?: string

  /**
   * Any additional classes to apply to the image.
   */
  imageClassName?: string

  /**
   * If present, force a text fallback, NOT a color avatar.
   */
  textFallback?: string

  /**
   * If present, force no fallback.
   */
  noFallback?: boolean

  /**
   * The determinstic ID to use.
   *
   * This is used to generate a deterministic color for the avatar.
   *
   * This should be a stable identifier for the user. Preferably user ID, but if this is someone with no Paragraph account,
   * use their email address or wallet address.
   */
  deterministicId?: string
}

/**
 * The de-facto way to render User avatars on Paragraph.
 *
 * The order of operations is as follows:
 *
 * - Avatar URL defined? Use that.
 * - No Avatar URL defined? Use the deterministic ID to generate a color avatar, based on a fixed ordering
 * of identifiers.
 *
 */
export function AuthorAvatar(props: {
  user: Partial<User> | BaseUserPublic
  size: number
  className?: string
  noFallback?: boolean
  fallbackClassName?: string
}) {
  // Strongly prefer deterministic user ID, since we guarantee it's unique.
  let deterministicId = props?.user?.id

  let wallet = (
    "lowercase_wallet_address" in props.user
      ? props.user.lowercase_wallet_address
      : "wallet_address" in props.user
        ? props.user.wallet_address
        : "walletAddress" in props.user
          ? props.user.walletAddress
          : null
  ) as string

  if (!deterministicId) {
    if ("email" in props.user && props.user.email) {
      deterministicId = props.user.email
    } else if (wallet) {
      deterministicId = wallet.toLowerCase()
    } else if (props.user.authorName) {
      deterministicId = props.user.authorName
    }
  }

  const alt = props.user.displayName?.fullName || props.user.authorName

  return (
    <Avatar
      src={props.user.avatar_url}
      alt={alt}
      size={props.size}
      className={props.className}
      deterministicId={deterministicId}
      noFallback={props.noFallback}
      fallbackClassName={props.fallbackClassName}
    />
  )
}

export default function Avatar({
  src,
  alt,
  size,
  className,
  imageClassName,
  fallbackClassName,
  textFallback,
  noFallback,
  deterministicId,
}: Props) {
  if (!src && noFallback) return null

  if (!src) {
    const fallbackId = (
      deterministicId ||
      alt ||
      Math.random().toString()
    ).toLowerCase()

    return (
      <div
        className={cn(
          "flex justify-center items-center rounded-full text-foreground my-auto",
          className,
          fallbackClassName
        )}
      >
        {textFallback ? (
          <span className="bg-muted rounded-md px-2">{textFallback}</span>
        ) : (
          <EmptyAvatar className={className} input={fallbackId} />
        )}
      </div>
    )
  }

  return (
    <ShadcnAvatar className={cn("transition-all my-auto", className)}>
      <AvatarImage asChild src={ONE_PIXEL_SVG}>
        <Image
          src={src}
          alt={alt || "Avatar"}
          width={size}
          height={size}
          className={cn(
            "overflow-hidden rounded-full h-full w-full",
            imageClassName
          )}
          sizes={`${size}px`}
          quality={90}
        />
      </AvatarImage>
    </ShadcnAvatar>
  )
}
